import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS } from "@/lib/site-data";
import { scrollToId } from "@/lib/useLenis";

const HEADER_INITIAL = { y: -30, opacity: 0 };
const HEADER_VISIBLE = { y: 0, opacity: 1 };
const HEADER_TRANSITION = { duration: 0.9, delay: 0.2, ease: [0.76, 0, 0.24, 1] };

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    const l = typeof link === "string" ? { id: link } : link;
    setOpen(false);
    if (l.href) {
      navigate(l.href);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: l.id } });
      return;
    }
    setTimeout(() => scrollToId(l.id), 60);
  };

  return (
    <>
      <motion.header
        initial={HEADER_INITIAL}
        animate={HEADER_VISIBLE}
        transition={HEADER_TRANSITION}
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled
            ? "bg-background/80 backdrop-blur-xl border-b border-foreground/10 py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between">
          <NavLogo onClick={() => go("hero")} />
          <DesktopLinks go={go} />
          <div className="flex items-center gap-3">
            <a
              href="/#contact"
              onClick={(e) => { e.preventDefault(); go("contact"); }}
              data-testid="nav-cta"
              className="hidden md:inline-flex items-center gap-2 border border-foreground/80 px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-medium btn-sharp"
            >
              Plan a Journey
              <span aria-hidden>→</span>
            </a>
            <BurgerButton open={open} onToggle={() => setOpen((v) => !v)} />
          </div>
        </div>
      </motion.header>

      <MobileMenu open={open} go={go} />
    </>
  );
}

function NavLogo({ onClick }) {
  return (
    <a
      href="/"
      onClick={(e) => { e.preventDefault(); onClick(); }}
      aria-label="Planet Air Adventure — home"
      data-testid="nav-logo"
      className="flex items-center gap-3 group"
    >
      <span className="w-2 h-2 bg-primary rotate-45 group-hover:rotate-[225deg] transition-transform duration-700" />
      <span className="font-serif text-xl md:text-2xl tracking-tight leading-none">
        Planet Air<span className="italic text-primary"> Adventure</span>
      </span>
    </a>
  );
}

function DesktopLinks({ go }) {
  return (
    <nav className="hidden md:flex items-center gap-9">
      {NAV_LINKS.map((l) => (
        <a
          key={l.id}
          href={l.href || `/#${l.id}`}
          onClick={(e) => { e.preventDefault(); go(l); }}
          data-testid={`nav-link-${l.id}`}
          className="text-[11px] uppercase tracking-[0.22em] font-medium text-foreground/75 hover:text-foreground transition-colors relative group"
        >
          {l.label}
          <span className="absolute -bottom-1 left-0 w-0 h-px bg-primary group-hover:w-full transition-all duration-500" />
        </a>
      ))}
    </nav>
  );
}

function BurgerButton({ open, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="md:hidden flex flex-col gap-1.5 p-2"
      data-testid="nav-mobile-toggle"
      aria-label="Menu"
    >
      <span className={`w-6 h-px bg-foreground transition-transform ${open ? "translate-y-[7px] rotate-45" : ""}`} />
      <span className={`w-6 h-px bg-foreground transition-opacity ${open ? "opacity-0" : ""}`} />
      <span className={`w-6 h-px bg-foreground transition-transform ${open ? "-translate-y-[6px] -rotate-45" : ""}`} />
    </button>
  );
}

function MobileMenu({ open, go }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 bg-background md:hidden pt-24 px-6"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-6">
            {NAV_LINKS.map((l, i) => (
              <motion.a
                key={l.id}
                href={l.href || `/#${l.id}`}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: i * 0.05 + 0.1 }}
                onClick={(e) => { e.preventDefault(); go(l); }}
                className="block text-left font-serif text-4xl border-b border-foreground/10 pb-4"
                data-testid={`mobile-link-${l.id}`}
              >
                {l.label}
              </motion.a>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
